import { isSameMonth } from 'date-fns/isSameMonth';
import bigDecimal from 'js-big-decimal';
import { ReactNode } from 'react';
import { v4 as uuid } from 'uuid';

import { format, getZonedDate } from '@eika-infrastruktur/date';
import ValutaFilter from '@eika-infrastruktur/ui-valuta-filter';
import { FormattedTransaction, FormattedTransactions } from '@eika/transaction-list/src/TransactionList';

import type { Transaction } from '../../generated-types';

const labelDateFormat = 'dd.MM';
const labelMonth = 'MMMM';
const labelYear = 'yyyy';

const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
};

const getDate = (transaction: Transaction) => {
    return getZonedDate(transaction.bookingDate || transaction.createdDate);
};

const getPreviousTransactionDate = (transactions: Transaction[], index: number) => {
    const previous = index - 1;
    if (previous < 0 || transactions[previous] === null) return null;
    return getDate(transactions[previous]);
};

const isNewMonth = (transaction: Transaction, transactions: Transaction[], index: number): boolean => {
    const prevDate = getPreviousTransactionDate(transactions, index);
    const date = getDate(transaction);
    return prevDate === null || !isSameMonth(date, prevDate);
};

const isNewYear = (transaction: Transaction): boolean => {
    const date = getZonedDate();
    const transactionDate = getDate(transaction);
    return date.getFullYear() !== transactionDate.getFullYear();
};

const isNewYearGroup = (transaction: Transaction, transactions: Transaction[], index: number): boolean => {
    const prevDate = getPreviousTransactionDate(transactions, index);
    const date = getDate(transaction);
    return prevDate === null || date.getFullYear() !== prevDate.getFullYear();
};

const getMonthLabel = (t: Transaction) => {
    return capitalize(format(getDate(t), labelMonth));
};
const getYearLabel = (t: Transaction) => {
    return format(getDate(t), labelYear);
};

const getSubDescription = (
    transaction: Transaction,
    textShown: boolean,
    enablePurposeAsSubCategory = false,
): string | undefined => {
    if (!enablePurposeAsSubCategory) return undefined;
    if (transaction.purpose && transaction.purpose.toLowerCase() !== transaction.text.toLowerCase()) {
        return transaction.purpose;
    }
    if (!textShown) {
        return transaction.text;
    }
    return undefined;
};

const formatTransaction = (
    transaction: Transaction,
    onClick: (id: string) => void,
    getDescription: (transaction: Transaction) => ReactNode,
    differentiateDeposit = true,
    enablePurposeAsSubCategory = false,
): FormattedTransaction => {
    const description = getDescription(transaction);
    const textShown = description === transaction.text;
    return {
        id: transaction.transactionId?.value ?? uuid(),
        description,
        date: format(transaction.bookingDate || transaction.createdDate, labelDateFormat),
        amount: ValutaFilter(transaction.transactionAmount),
        deposit: differentiateDeposit && transaction.transactionAmount > 0,
        reservation: transaction.status === 'RESERVED',
        onClick: transaction.transactionId ? onClick : undefined,
        balance: transaction.status !== 'RESERVED' ? ValutaFilter(transaction.accountBalance) : undefined,
        subDescription: getSubDescription(transaction, textShown, enablePurposeAsSubCategory),
    };
};

const getMonth = (transaction: Transaction) => {
    const monthLabel = getMonthLabel(transaction);
    const yearLabel = getYearLabel(transaction);
    const id = monthLabel + yearLabel;
    let label = monthLabel;
    if (isNewYear(transaction)) {
        label = label.concat(` ${yearLabel}`);
    }
    return {
        id,
        label,
        transactions: [],
    };
};

export const sortByReservedTransactions = (transactions: Transaction[]): Transaction[] => {
    return transactions.slice().sort((a, b) => {
        const dateA = getDate(a);
        const dateB = getDate(b);
        if (isSameMonth(dateA, dateB)) {
            if (a.status === b.status) return 0;
            return a.status === 'RESERVED' ? -1 : 1;
        }
        return 0;
    });
};

const addUniqueKeyTransactions = (transactions: FormattedTransaction[]) => {
    return transactions.reduce((list, current) => {
        if (list.some((t) => t.id === current.id)) {
            return list.concat({ ...current, key: uuid() });
        }
        return list.concat(current);
    }, [] as FormattedTransaction[]);
};

const addUniqueKey = (month: FormattedTransactions) => ({
    ...month,
    transactions: addUniqueKeyTransactions(month.transactions),
});

const getPreviousTransaction = (transactions: Transaction[] | undefined): Transaction | undefined =>
    transactions && transactions.length > 0 ? transactions[transactions.length - 1] : undefined;

export function addRunningBalance(transactions: Transaction[], startingBalance: number | undefined): Transaction[] {
    if (startingBalance === undefined) return transactions;
    return transactions.reduce((last, current) => {
        const previousTrans = getPreviousTransaction(last);
        if (current.accountBalance !== undefined) {
            return [...last, current];
        }
        const prevousAccountingBalance = previousTrans?.accountBalance ?? startingBalance;
        const accountBalance =
            previousTrans?.status !== 'RESERVED'
                ? parseFloat(bigDecimal.subtract(prevousAccountingBalance, previousTrans?.transactionAmount))
                : prevousAccountingBalance;

        return [
            ...last,
            {
                ...current,
                accountBalance,
            },
        ];
    }, [] as Transaction[]);
}

export default function formatTransactions(
    transactions: Transaction[],
    onClick: (id: string) => void,
    startBalance: number | undefined,
    groupByYear?: boolean,
    differentiateDeposits?: boolean,
    renderDescription: (transaction: Transaction) => ReactNode | string = (transaction) => transaction.text,
    enablePurposeAsSubCategory = false,
): FormattedTransactions[] {
    const initialValue: FormattedTransactions[] = [];

    const sortedTransactions = sortByReservedTransactions(transactions);

    return addRunningBalance(sortedTransactions, startBalance)
        .reduce((map, transaction, currentIndex, orgTrans) => {
            if (groupByYear && isNewYearGroup(transaction, orgTrans, currentIndex)) {
                const yearLabel = getYearLabel(transaction);
                map.push({ id: yearLabel, label: yearLabel, transactions: [] });
            }
            if (!groupByYear && isNewMonth(transaction, orgTrans, currentIndex)) {
                map.push(getMonth(transaction));
            }
            map[map.length - 1].transactions.push(
                formatTransaction(
                    transaction,
                    onClick,
                    renderDescription,
                    differentiateDeposits,
                    enablePurposeAsSubCategory,
                ),
            );
            return map;
        }, initialValue)
        .map(addUniqueKey);
}
